import { useMemo } from "react";

import { RVSideNavLayout, type PageSubNavItem } from "@regal-voice/ui";
import { usePageAvailability } from "App/routes/Hooks/usePageAvilability/usePageAvailability";
import { AppPagePaths } from "App/routes/paths";
import { AgentSettingsPaths } from "Pages/settings/Pages/AgentSettings/Pages/paths";
import { isRegalVoiceUser } from "Services/AuthService";
import { useFlags } from "Services/FeatureFlagService";
import { CollapsiblePanel } from "Utils/CollapsiblePanels";
import { useKnownPanels } from "Utils/CollapsiblePanels/knownPanels";

import { ADSettingsPaths } from "../Pages/AgentDesktop/paths";
import { ComplianceSettingsPaths } from "../Pages/ComplianceSettings/paths";
import { EventManagementPaths } from "../Pages/DataManagement/EventManagement/paths";
import { SIPPaths } from "../Pages/sip/paths";
import { TrustPaths } from "../Pages/Trust/paths";
import { SettingsPaths, USER_MANAGEMENT_PATH } from "../paths";
import { SETTINGS_NAV_TITLES, SETTINGS_NAV_TITLES_PATCH } from "../settingsMetadata";

type SettingsNavFlags = {
    showRoutingRules: boolean;
    showUserManagement: boolean;
    showEmail: boolean;
    showPreRecordedVmDrops: boolean;
    showAgentPermissions: boolean;
    showAgentSettings: boolean;
    showBusinessProfiles: boolean;
    showBrandedNumbers: boolean;
    showCustomUserAttributes: boolean;
    showTrustOverview: boolean;
    showBrandSecrets: boolean;
};

function getReorganizedSettingsItems(flags: SettingsNavFlags): Array<PageSubNavItem> {
    const items: Array<PageSubNavItem> = [];

    items.push({
        key: SettingsPaths.generalSettings,
        title: SETTINGS_NAV_TITLES[SettingsPaths.generalSettings],
        href: `/${SettingsPaths.generalSettings}`,
    });

    items.push({
        key: "userManagement",
        title: SETTINGS_NAV_TITLES[USER_MANAGEMENT_PATH],
        href: `/${SettingsPaths.userManagementPageUsers}`,
        isVisible: flags.showUserManagement,
        children: [
            {
                key: SettingsPaths.userManagementPageUsers,
                title: SETTINGS_NAV_TITLES[SettingsPaths.userManagementPageUsers],
                href: `/${SettingsPaths.userManagementPageUsers}`,
                tabs: [
                    `/${SettingsPaths.userManagementPageUsers}`,
                    `/${SettingsPaths.userManagementNewUser}`,
                    `/${SettingsPaths.userManagementEditUser}`,
                ],
            },
            {
                key: SettingsPaths.userManagementPageTeams,
                title: SETTINGS_NAV_TITLES[SettingsPaths.userManagementPageTeams],
                href: `/${SettingsPaths.userManagementPageTeams}`,
                tabs: [
                    `/${SettingsPaths.userManagementPageTeams}`,
                    `/${SettingsPaths.userManagementTeam}`,
                    `/${SettingsPaths.userManagementTeamEdit}`,
                ],
            },
            {
                key: SettingsPaths.userManagementPageSkills,
                title: SETTINGS_NAV_TITLES[SettingsPaths.userManagementPageSkills],
                href: `/${SettingsPaths.userManagementPageSkills}`,
                tabs: [
                    `/${SettingsPaths.userManagementPageSkills}`,
                    `/${SettingsPaths.userManagementSkillsNew}`,
                    `/${SettingsPaths.userManagementSkillsEdit}`,
                ],
            },
            {
                key: SettingsPaths.userManagementPageCustomAttributes,
                title: SETTINGS_NAV_TITLES[SettingsPaths.userManagementPageCustomAttributes],
                href: `/${SettingsPaths.userManagementPageCustomAttributes}`,
                tabs: [
                    `/${SettingsPaths.userManagementPageCustomAttributes}`,
                    `/${SettingsPaths.userManagementCustomAttributesNew}`,
                    `/${SettingsPaths.userManagementCustomAttributesEdit}`,
                ],
                isVisible: flags.showCustomUserAttributes,
            },
        ],
        tabs: [
            `/${SettingsPaths.userManagementPageUsers}`,
            `/${SettingsPaths.userManagementPageTeams}`,
            `/${SettingsPaths.userManagementPageSkills}`,
            `/${SettingsPaths.userManagementPageCustomAttributes}`,
            `/${SettingsPaths.userManagementNewUser}`,
            `/${SettingsPaths.userManagementEditUser}`,
            `/${SettingsPaths.userManagementTeam}`,
            `/${SettingsPaths.userManagementTeamEdit}`,
            `/${SettingsPaths.userManagementSkillsNew}`,
            `/${SettingsPaths.userManagementSkillsEdit}`,
        ],
    });

    items.push({
        key: SettingsPaths.complianceSettings,
        title: SETTINGS_NAV_TITLES[SettingsPaths.complianceSettings],
        href: `/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.contactRestrictions}`,
        children: [
            {
                key: ComplianceSettingsPaths.contactRestrictions,
                title: SETTINGS_NAV_TITLES[ComplianceSettingsPaths.contactRestrictions],
                href: `/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.contactRestrictions}`,
            },
            {
                key: ComplianceSettingsPaths.holidays,
                title: SETTINGS_NAV_TITLES[ComplianceSettingsPaths.holidays],
                href: `/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.holidays}`,
            },
            {
                key: ComplianceSettingsPaths.callLimits,
                title: SETTINGS_NAV_TITLES[ComplianceSettingsPaths.callLimits],
                href: `/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.callLimits}`,
            },
            {
                key: ComplianceSettingsPaths.stateOfEmergency,
                title: SETTINGS_NAV_TITLES[ComplianceSettingsPaths.stateOfEmergency],
                href: `/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.stateOfEmergency}`,
            },
        ],
        tabs: [
            `/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.contactRestrictions}`,
            `/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.holidays}`,
            `/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.callLimits}`,
            `/${SettingsPaths.complianceSettings}/${ComplianceSettingsPaths.stateOfEmergency}`,
        ],
    });

    items.push({
        key: SettingsPaths.phoneNumbers,
        title: SETTINGS_NAV_TITLES[SettingsPaths.phoneNumbers],
        href: `/${SettingsPaths.phoneNumbers}`,
        tabs: [`/${SettingsPaths.phoneNumbers}`],
    });

    items.push({
        key: SettingsPaths.externalTransferPhonebook,
        title: SETTINGS_NAV_TITLES[SettingsPaths.externalTransferPhonebook],
        href: `/${SettingsPaths.externalTransferPhonebook}`,
    });

    items.push({
        key: SettingsPaths.trust,
        title: SETTINGS_NAV_TITLES[SettingsPaths.trust],
        href: flags.showTrustOverview
            ? `/${SettingsPaths.trust}/${TrustPaths.trustOverview}`
            : `/${SettingsPaths.trust}/${TrustPaths.businessProfiles}`,
        isVisible: flags.showBusinessProfiles || flags.showBrandedNumbers || flags.showTrustOverview,
        children: [
            {
                key: TrustPaths.trustOverview,
                title: SETTINGS_NAV_TITLES[TrustPaths.trustOverview],
                href: `/${SettingsPaths.trust}/${TrustPaths.trustOverview}`,
                isVisible: flags.showTrustOverview,
            },
            {
                key: TrustPaths.businessProfiles,
                title: SETTINGS_NAV_TITLES[TrustPaths.businessProfiles],
                href: `/${SettingsPaths.trust}/${TrustPaths.businessProfiles}`,
                isVisible: flags.showBusinessProfiles,
            },
            {
                key: TrustPaths.brandingAndSpam,
                title: SETTINGS_NAV_TITLES[TrustPaths.brandingAndSpam],
                href: `/${SettingsPaths.trust}/${TrustPaths.brandingAndSpam}`,
                isVisible: flags.showBrandedNumbers,
            },
        ],
        tabs: [
            `/${SettingsPaths.trust}/${TrustPaths.trustOverview}`,
            `/${SettingsPaths.trust}/${TrustPaths.businessProfiles}`,
            `/${SettingsPaths.trust}/${TrustPaths.brandingAndSpam}`,
        ],
    });

    items.push({
        key: SettingsPaths.recordings,
        title: SETTINGS_NAV_TITLES[SettingsPaths.recordings],
        href: `/${SettingsPaths.recordings}`,
    });

    items.push({
        key: SettingsPaths.sip,
        title: SETTINGS_NAV_TITLES[SettingsPaths.sip],
        href: `/${SettingsPaths.sip}/${SIPPaths.sipDomains}`,
        isVisible: isRegalVoiceUser(),
        children: [
            {
                key: SIPPaths.sipDomains,
                title: SETTINGS_NAV_TITLES[SIPPaths.sipDomains],
                href: `/${SettingsPaths.sip}/${SIPPaths.sipDomains}`,
                tabs: [
                    `/${SettingsPaths.sip}/${SIPPaths.sipDomains}`,
                    `/${SettingsPaths.sip}/${SIPPaths.sipDomainDetails}`,
                ],
            },
            {
                key: SIPPaths.ipAccessControlLists,
                title: SETTINGS_NAV_TITLES[SIPPaths.ipAccessControlLists],
                href: `/${SettingsPaths.sip}/${SIPPaths.ipAccessControlLists}`,
            },
        ],
        tabs: [
            `/${SettingsPaths.sip}/${SIPPaths.sipDomains}`,
            `/${SettingsPaths.sip}/${SIPPaths.sipDomainDetails}`,
            `/${SettingsPaths.sip}/${SIPPaths.ipAccessControlLists}`,
        ],
    });

    items.push({
        key: SettingsPaths.yourData,
        title: SETTINGS_NAV_TITLES[SettingsPaths.yourData],
        href: `/${SettingsPaths.objectTypes}`,
        children: [
            {
                key: SettingsPaths.objectTypes,
                title: SETTINGS_NAV_TITLES[SettingsPaths.objectTypes],
                href: `/${SettingsPaths.objectTypes}`,
                tabs: [
                    `/${SettingsPaths.yourData}`,
                    `/${SettingsPaths.objectTypes}`,
                    `/${SettingsPaths.customDefinition}`,
                ],
                isVisible: true,
            },
            {
                key: SettingsPaths.eventManagement,
                title: SETTINGS_NAV_TITLES[SettingsPaths.eventManagement],
                href: `/${SettingsPaths.eventManagement}`,
                tabs: [`/${SettingsPaths.eventManagement}/${EventManagementPaths.eventForm}`],
            },
        ],
    });

    items.push({
        key: SettingsPaths.integrations,
        title: SETTINGS_NAV_TITLES[SettingsPaths.integrations],
        href: `/${SettingsPaths.integrations}`,
    });

    items.push({
        key: SettingsPaths.mySettings,
        title: SETTINGS_NAV_TITLES[SettingsPaths.mySettings],
        href: `/${SettingsPaths.mySettings}`,
        children: [
            {
                // No SettingsPaths equivalent for my-settings child resources.
                key: AgentSettingsPaths.integrations,
                title: SETTINGS_NAV_TITLES_PATCH[AgentSettingsPaths.integrations],
                href: `/${SettingsPaths.mySettings}/${AgentSettingsPaths.integrations}`,
                isVisible: flags.showEmail,
            },
            {
                // No SettingsPaths equivalent for my-settings child resources.
                key: AgentSettingsPaths.recordings,
                title: SETTINGS_NAV_TITLES[AgentSettingsPaths.recordings],
                href: `/${SettingsPaths.mySettings}/${AgentSettingsPaths.recordings}`,
                isVisible: flags.showPreRecordedVmDrops,
            },
        ],
        tabs: [
            `/${SettingsPaths.mySettings}/${AgentSettingsPaths.integrations}`,
            `/${SettingsPaths.mySettings}/${AgentSettingsPaths.recordings}`,
        ],
        isVisible: flags.showAgentSettings,
    });

    items.push({
        key: SettingsPaths.agentDesktop,
        title: SETTINGS_NAV_TITLES[SettingsPaths.agentDesktop],
        href: `/${ADSettingsPaths.taskPreferences}`,
        children: [
            {
                // No SettingsPaths equivalent for agent-desktop child resources.
                key: ADSettingsPaths.taskPreferences,
                title: SETTINGS_NAV_TITLES[ADSettingsPaths.taskPreferences],
                href: `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.taskPreferences}`,
                tabs: [
                    `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.taskPreferences}`,
                    `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.taskPreferencesForm}`,
                ],
            },
            {
                // No SettingsPaths equivalent for agent-desktop child resources.
                key: ADSettingsPaths.dispositions,
                title: SETTINGS_NAV_TITLES[ADSettingsPaths.dispositions],
                href: `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.dispositions}`,
            },
            {
                // No SettingsPaths equivalent for agent-desktop child resources.
                key: ADSettingsPaths.tags,
                title: SETTINGS_NAV_TITLES[ADSettingsPaths.tags],
                href: `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.tags}`,
            },
            {
                // No SettingsPaths equivalent for agent-desktop child resources.
                key: ADSettingsPaths.cancellationReasons,
                title: SETTINGS_NAV_TITLES[ADSettingsPaths.cancellationReasons],
                href: `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.cancellationReasons}`,
            },
            {
                // No SettingsPaths equivalent for agent-desktop child resources.
                key: ADSettingsPaths.activityStatuses,
                title: SETTINGS_NAV_TITLES[ADSettingsPaths.activityStatuses],
                href: `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.activityStatuses}`,
            },
            {
                // No SettingsPaths equivalent for agent-desktop child resources.
                key: ADSettingsPaths.templates,
                title: SETTINGS_NAV_TITLES[ADSettingsPaths.templates],
                href: `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.templates}`,
                tabs: [
                    `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.templates}`,
                    `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.templates}/:templateId`,
                ],
            },
            {
                // No SettingsPaths equivalent for agent-desktop child resources.
                key: ADSettingsPaths.agentPermissions,
                title: SETTINGS_NAV_TITLES[ADSettingsPaths.agentPermissions],
                href: `/${SettingsPaths.agentDesktop}/${ADSettingsPaths.agentPermissions}`,
                isVisible: flags.showAgentPermissions,
            },
        ],
    });

    items.push({
        key: SettingsPaths.contactLists,
        title: SETTINGS_NAV_TITLES[SettingsPaths.contactLists],
        href: `/${SettingsPaths.contactLists}`,
    });

    items.push({
        key: SettingsPaths.brandSecrets,
        title: SETTINGS_NAV_TITLES[SettingsPaths.brandSecrets],
        href: `/${SettingsPaths.brandSecrets}`,
        isVisible: flags.showBrandSecrets,
    });

    return items;
}

export function SettingsSideNavBar(): JSX.Element {
    const { settingsPages } = usePageAvailability();
    const {
        showRoutingRules,
        showUserManagement,
        showEmail,
        showPreRecordedVmDrops,
        showAgentPermissions,
        showAgentSettings,
        showBusinessProfiles,
        showBrandedNumbers,
        showTrustOverview,
    } = settingsPages;
    const { showCustomUserAttributes, showBrandSecrets } = useFlags();

    const settings: Array<PageSubNavItem> = useMemo(() => {
        const flags: SettingsNavFlags = {
            showRoutingRules,
            showUserManagement,
            showEmail,
            showPreRecordedVmDrops,
            showAgentPermissions,
            showAgentSettings,
            showBusinessProfiles,
            showBrandedNumbers,
            showCustomUserAttributes,
            showTrustOverview,
            showBrandSecrets,
        };

        return getReorganizedSettingsItems(flags);
    }, [
        showRoutingRules,
        showUserManagement,
        showEmail,
        showPreRecordedVmDrops,
        showAgentPermissions,
        showAgentSettings,
        showBusinessProfiles,
        showBrandedNumbers,
        showCustomUserAttributes,
        showTrustOverview,
        showBrandSecrets,
    ]);

    const { settings: settingsPanel } = useKnownPanels();

    return (
        <CollapsiblePanel panelName={settingsPanel.name} expandedWidth={settingsPanel.width}>
            <RVSideNavLayout sidenavTitle="Settings" items={settings} basePath={AppPagePaths.settings} hasSubPages />
        </CollapsiblePanel>
    );
}
